// Thin wrapper around window.Telegram.WebApp.
// Loaded as a script tag in index.html (not via npm) because Telegram
// pins their SDK URL and any bundled copy goes stale quickly.

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp }
  }
}

interface TelegramWebApp {
  initData: string
  initDataUnsafe?: {
    user?: { id: number; first_name?: string; language_code?: string }
    start_param?: string
  }
  colorScheme: 'light' | 'dark'
  themeParams: { bg_color?: string; text_color?: string }
  viewportHeight: number
  viewportStableHeight: number
  isExpanded: boolean
  ready: () => void
  expand: () => void
  close: () => void
  setHeaderColor: (color: string) => void
  setBackgroundColor: (color: string) => void
  openLink: (url: string) => void
  openTelegramLink: (url: string) => void
  onEvent: (event: string, cb: () => void) => void
  offEvent: (event: string, cb: () => void) => void
  BackButton: {
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
  MainButton?: {
    setText: (text: string) => void
    show: () => void
    hide: () => void
    enable: () => void
    disable: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
    showProgress?: (leaveActive?: boolean) => void
    hideProgress?: () => void
  }
  CloudStorage?: {
    setItem: (k: string, v: string, cb?: (err: Error | null) => void) => void
    getItem: (k: string, cb: (err: Error | null, v: string | null) => void) => void
    removeItem: (k: string, cb?: (err: Error | null) => void) => void
  }
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy') => void
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void
  }
}

let _wa: TelegramWebApp | null = null

export function getWebApp(): TelegramWebApp | null {
  if (_wa) return _wa
  const w = typeof window !== 'undefined' ? window.Telegram?.WebApp : null
  if (w) _wa = w
  return _wa
}

/** True only inside a real Telegram client. The SDK shim loads in a plain
 *  browser too (and exposes MainButton/BackButton that render nothing), so we
 *  key on a signed, non-empty `initData` — present only in real Telegram. Lets
 *  screens fall back to in-app back/submit buttons during browser dev. */
export function isTelegramEnv(): boolean {
  return !!getWebApp()?.initData
}

/**
 * Init the Telegram SDK before first render.
 *
 * Order is critical and matches docs/miniapp-m1-tz.md §16.1:
 *   1. ready()  — signals to Telegram we have layout ready
 *   2. expand() — open to full viewport
 *   3. setHeaderColor/setBackgroundColor — sync native chrome
 *   4. theme listener — react to runtime theme changes
 *
 * Safe to call even when running outside Telegram (returns false).
 */
export function initTelegramWebApp(): boolean {
  const wa = getWebApp()
  if (!wa) return false

  try {
    wa.ready()
  } catch (e) {
    // ready() throws in some Telegram desktop builds when called too early.
    // Non-fatal — subsequent calls still work.
    console.warn('[tg] ready() failed:', e)
  }
  try { wa.expand() } catch {}

  applyTheme()

  const setVar = () => {
    const px = wa.viewportStableHeight || wa.viewportHeight || window.innerHeight
    document.documentElement.style.setProperty(
      '--tg-viewport-stable-height',
      `${px}px`,
    )
  }
  setVar()
  wa.onEvent('viewportChanged', setVar)
  // Our theme is a fixed Light/Dark choice (seeded once at boot), so a Telegram
  // theme change no longer switches the app — this re-apply just re-asserts our
  // header/background colors if Telegram repaints its native chrome.
  wa.onEvent('themeChanged', () => applyTheme())

  return true
}

// ── Theme ───────────────────────────────────────────────────────────────────
// Only Light/Dark are offered. First-run: seed from Telegram's colorScheme;
// after that the stored choice wins. Persisted preference lives in prefs.ts
// (CloudStorage); it calls setThemeOverride() to push the value here on boot + on change.
export type ThemeOverride = 'light' | 'dark'
// Only Light/Dark are selectable. "Follow Telegram" survives just as the
// first-run seed: an explicit saved choice wins; otherwise we adopt Telegram's
// current colorScheme once. CloudStorage (cross-device) reconciles a tick later
// in prefs.hydrate(). A legacy stored 'system' (no longer valid) falls through
// to the Telegram seed.
let _themePref: ThemeOverride = (() => {
  try {
    const v = localStorage.getItem('pt:theme')
    if (v === 'light' || v === 'dark') return v
  } catch { /* private mode */ }
  return getWebApp()?.colorScheme === 'dark' ? 'dark' : 'light'
})()

// Dev-only ?theme=light|dark query — an explicit force for browser previews
// that wins over the stored preference (so a deep-link screenshot stays put).
function devThemeOverride(): 'light' | 'dark' | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null
  const v = new URLSearchParams(window.location.search).get('theme')
  return v === 'dark' || v === 'light' ? v : null
}

function effectiveScheme(): 'light' | 'dark' {
  return devThemeOverride() ?? _themePref
}

/** (Re)apply the resolved theme to <html> + Telegram's native chrome. */
export function applyTheme(): void {
  const isDark = effectiveScheme() === 'dark'
  document.documentElement.classList.toggle('theme-dark', isDark)
  const wa = getWebApp()
  if (!wa) return
  // Theme is always our own Light/Dark palette now (no "follow Telegram" mode),
  // so the native bar uses our bg directly.
  const bg = isDark ? '#0F1620' : '#F6F8FB'
  try { wa.setHeaderColor(bg) } catch {}
  try { wa.setBackgroundColor(bg) } catch {}
}

/** Set the active theme preference and re-render the palette immediately. */
export function setThemeOverride(pref: ThemeOverride): void {
  _themePref = pref
  applyTheme()
}

// CloudStorage promisified, ALWAYS mirrored to localStorage. CloudStorage syncs
// across devices but is async and can be flaky on some clients; the localStorage
// mirror is synchronous and reliable on-device, so a saved theme/language sticks
// (and is readable before first paint) even if CloudStorage drops the value.
// Per docs/miniapp-m1-tz.md §4.2.
function lsGet(key: string): string | null {
  try { return localStorage.getItem(`pt:${key}`) } catch { return null }
}
function lsSet(key: string, value: string): void {
  try { localStorage.setItem(`pt:${key}`, value) } catch { /* private mode / quota */ }
}

export const tgStorage = {
  async set(key: string, value: string): Promise<void> {
    lsSet(key, value) // synchronous, reliable mirror — write first
    const cs = getWebApp()?.CloudStorage
    if (!cs) return
    // Best-effort cloud write; localStorage already holds the value, so a cloud
    // failure must not reject (callers would treat it as "not saved").
    return new Promise((resolve) => {
      cs.setItem(key, value, () => resolve())
    })
  },
  async get(key: string): Promise<string | null> {
    const local = lsGet(key)
    const cs = getWebApp()?.CloudStorage
    if (!cs) return local
    return new Promise((resolve) => {
      cs.getItem(key, (err, val) =>
        resolve(!err && val != null && val !== '' ? val : local),
      )
    })
  },
  async remove(key: string): Promise<void> {
    try { localStorage.removeItem(`pt:${key}`) } catch { /* ignore */ }
    const cs = getWebApp()?.CloudStorage
    if (!cs) return
    return new Promise((resolve) => {
      cs.removeItem(key, () => resolve())
    })
  },
}

// Deeplink payload — Telegram passes ?startapp=<x> as initDataUnsafe.start_param.
// In dev (no Telegram) we fall back to a ?start_param= query arg so deeplinks
// can be exercised in the browser.
export function getStartParam(): string {
  const fromTg = getWebApp()?.initDataUnsafe?.start_param
  if (fromTg) return fromTg
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search).get('start_param') || ''
  }
  return ''
}

// Open a t.me link inside Telegram (or a normal tab in dev).
export function openTelegramLink(url: string): void {
  const wa = getWebApp()
  if (wa) {
    try { wa.openTelegramLink(url); return } catch { /* fall through */ }
  }
  try { window.open(url, '_blank', 'noopener') } catch { /* noop */ }
}

// Open an external URL (Bitrix, tel:) — leaves the Mini App webview.
export function openExternalLink(url: string): void {
  const wa = getWebApp()
  if (wa && !url.startsWith('tel:')) {
    try { wa.openLink(url); return } catch { /* fall through */ }
  }
  try { window.open(url, url.startsWith('tel:') ? '_self' : '_blank', 'noopener') } catch { /* noop */ }
}

// Haptic helpers — no-op outside Telegram.
export function hapticImpact(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  try { getWebApp()?.HapticFeedback?.impactOccurred(style) } catch { /* noop */ }
}
export function hapticNotify(type: 'success' | 'error' | 'warning'): void {
  try { getWebApp()?.HapticFeedback?.notificationOccurred(type) } catch { /* noop */ }
}

export function setBackButton(
  show: boolean,
  onClick?: () => void,
): () => void {
  const wa = getWebApp()
  if (!wa) return () => {}
  if (show) {
    if (onClick) wa.BackButton.onClick(onClick)
    wa.BackButton.show()
  } else {
    wa.BackButton.hide()
  }
  return () => {
    if (onClick) wa.BackButton.offClick(onClick)
    wa.BackButton.hide()
  }
}
